import { OfferPriority } from './../../models/class/offerPriority/offerPriority';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FullOffer, ImageOffer } from 'app/shared/models/class/offer/offer';
import { PricingOffer } from 'app/shared/models/class/pricing-offer/pricingOffer';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

import * as FileSaver from 'file-saver';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OfferService {

  private dataOffer: any;

  constructor(
    private readonly http: HttpClient
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                                     GET                                    */
  /* -------------------------------------------------------------------------- */


  getOffersByText(text: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/offer/all?text=${text}`);
  }

  getOffersByDateText(date: string, text?: string): Observable<any> {
    if (text) {
      return this.http.get(`${environment.apiUrl}/offer/all?date=${date}&text=${text}`);
    } else {
      return this.http.get(`${environment.apiUrl}/offer/all?date=${date}`);
    }
  }

  getOffersByFilter(params: any): Observable<any> {
    let url = `${environment.apiUrl}/offer/all?date=${params.date}`;
    if (params.distance && params.lat && params.lng)
      url += `&distance=${params.distance}&latitude=${params.lat}&longitude=${params.lng}`;
    if (params.tags && params.clubs)
      url += `&tags=${params.tags}&actors=${params.clubs}`;
    if (params.text)
      url += `&text=${params.text}`;

    return this.http.get(url);
  }

  getOffer(structure?: string, universe?: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/offer/offers/${structure || ' '}/${universe}`);
  }

  getFullOffer(clubCode?: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/Offer/FullOffers/${clubCode || ''}`);
  }

  getOfferById(offerKey: number): Observable<FullOffer> {
    return this.http.get<FullOffer>(`${environment.apiUrl}/Offer/FullOfferByOfferKey/${offerKey}`);
  }

  getOfferByClub(clubCode: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/Offer/OffersByClub/${clubCode}`);
  }

  getImgOffer(offerKey: number): Observable<ImageOffer> {
    return this.http.get<ImageOffer>(`${environment.apiUrl}/Offer/GetPicture/${offerKey}`);
  }

  getCarouselPictures(offerKey: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Offer/GetCarouselPictures/${offerKey}`);
  }

  getAltOfferPictures(offerKey: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Offer/AltOfferPicture/${offerKey}`);
  }

  getOfferPicturesZip(offerKey: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/Offer/OfferPicturesZip/${offerKey}`, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res, offerKey + '_pictures')));
  }
  // GET main pictures all offers
  getOfferMainPicturesZip(): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/Offer/OfferMainPictureZip`, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res, 'OfferMainPictures')));
  }

  getBoundDocumentsByOfferKey(offerKey: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/Offer/ExternLinks/${offerKey}`);
  }


  /* -------------------------------------------------------------------------- */
  /*                                    POST                                    */
  /* -------------------------------------------------------------------------- */

  addOffer(newOffer: FullOffer): Observable<any> {
    const formData = new FormData();
    formData.append('offerString', JSON.stringify(newOffer));

    return this.http.post<any>(`${environment.apiUrl}/Offer/NewOffer`, formData);
  }

  getOfferTag(tags: string, coordinates: string, structure?: string, universe?, actor?: string): Observable<FullOffer[]> {
    const formData = new FormData();
    formData.append('tags', tags);
    formData.append('coordinates', coordinates);

    let clubCode = sessionStorage.getItem("clubCodeForHomeOffers");
    let url = `${environment.apiUrl}/Offer/taggedOffersTest/${structure || ' '}/${universe}${clubCode ? '/' + clubCode : ''}`;
    if (actor) {
      url = `${environment.apiUrl}/Offer/taggedOffersTest/${structure || ' '}/${universe}${clubCode ? '/' + clubCode : ''}?actors=${actor}`
    }

    return this.http.post<FullOffer[]>(url, formData);
  }

  addOfferPricing(pricing: PricingOffer) {
    return this.http.post(`${environment.apiUrl}/Offer/NewOfferPricing`, pricing);
  }

  uploadImg(offerKey: number, files: File[], isMainPicture?: boolean) {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    const requestOptions: object = { responseType: 'text' };

    return this.http.post<any>(`${environment.apiUrl}/Offer/UploadPicture?offerKey=${offerKey}&&isMainPicture=${isMainPicture}`, formData, requestOptions);
  }

  uploadImages(offerKey: number, files: File[], isMainPicture?: boolean) {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    return this.http.post<any>(`${environment.apiUrl}/Offer/UploadPicture?offerKey=${offerKey}&&isMainPicture=${false}`, formData);
  }

  postBoundDocument(data: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/Offer/ExternLink`, data)
  }


  /* -------------------------------------------------------------------------- */
  /*                                     PUT                                    */
  /* -------------------------------------------------------------------------- */

  updateOffer(newOffer: FullOffer, file?: File): Observable<any> {

    const formData = new FormData();
    formData.append('offerString', JSON.stringify(newOffer));

    return this.http.put<any>(`${environment.apiUrl}/Offer/UpdateOffer`, formData);
  }

  activateOrDeactivateOffer(offerKey: number, isActive: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/ActivateOffer/${offerKey}/${isActive}`, null);
  }


  prioritizeOffer(offerKey: number, isPriority: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/PrioritizeOffer/${offerKey}/${isPriority}`, null);
  }

  updatePriorityOffers(updatePriority: OfferPriority) {
    const formData = new FormData();

    formData.append('clubCode', updatePriority.clubCode);
    formData.append('offerKeys', updatePriority.offerKeys);

    return this.http.put(`${environment.apiUrl}/Offer/UpdatePriorityOffers`, formData);
  }

  updateOfferPricing(pricing: PricingOffer) {
    return this.http.put(`${environment.apiUrl}/Offer/UpdateOfferPricing`, pricing);
  }

  updateExternalOffer(offerKey: number, isExternalOffer: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/ExternalOffer/${offerKey}/${isExternalOffer}`, null);
  }

  updateOfferHasSession(offerKey: number, hasSession: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/OfferHasSession/${offerKey}/${hasSession}`, null);
  }

  updateBoundDocument(data: any): Observable<any> {
    return this.http.put(`${environment.apiUrl}/Offer/ExternLink`, data)
  }

  /* -------------------------------------------------------------------------- */
  /*                                   DELETE                                   */
  /* -------------------------------------------------------------------------- */

  deleteOffer(offerKey: number) {
    return this.http.delete(`${environment.apiUrl}/offer/${offerKey}`);
  }

  deleteAltPictureOffer(offerPictureKey: number) {
    return this.http.delete(`${environment.apiUrl}/Offer/AltOfferPicture/${offerPictureKey}`);
  }

  deleteBoundDocument(key: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/Offer/ExternLink/${key}`);
  }

  /*
  ------------------------------- FILESAVER ----------------------------------
  */

  private saveFile(res: HttpResponse<any>, fileName: string): Blob {
    FileSaver.saveAs(res.body, fileName);
    return res.body;
  }


  setDataOffer(data: any): void { this.dataOffer = data; }
  getDataOffer(): any { return this.dataOffer; }
}

